import styled from 'styled-components';

import { WAREHOUSE_RETURN_PLURAL_STATES_LABELS } from './constants/states';

import {
    WarehouseReturnState,
    useGetWarehouseReturnForDateQuery,
    useWarehouseReturnCreatedOrUpdatedSubscription,
} from 'data/__generated__';

import { Loader } from 'components/Loader';
import { TrelloView } from 'components/TrelloView/TrelloView';
import { WarehouseReturnTrelloItem } from './components/WarehouseReturnTrelloItem';

import { formatDateAsAnniversary } from 'helpers/dateTimes';
import { warehouseReturnCreatedOrUpdatedHandler } from './cacheHandlers/warehouseReturnsCacheHandler';

export const WarehouseReturnsTrelloView = ({ warehouseReturnsDate }: { warehouseReturnsDate: Date }) => {
    const {
        data: warehouseReturnsData,
        error: warehouseReturnsError,
        loading: warehouseReturnLoading,
    } = useGetWarehouseReturnForDateQuery({
        fetchPolicy: 'cache-and-network',
        variables: { date: formatDateAsAnniversary({ dateTime: warehouseReturnsDate, useNewFormat: true }) },
    });

    useWarehouseReturnCreatedOrUpdatedSubscription({
        onData({ data: { data } }) {
            if (!data) {
                return;
            }
            const { warehouseReturnCreatedOrUpdated } = data;
            warehouseReturnCreatedOrUpdatedHandler(warehouseReturnCreatedOrUpdated);
        },
    });

    if (warehouseReturnLoading && !warehouseReturnsData) {
        return (
            <Content>
                <Loader />
            </Content>
        );
    }

    if (warehouseReturnsError || !warehouseReturnsData) {
        throw new Error('Une erreur est survenue lors de la récupération des rangements');
    }

    const { getWarehouseReturnsForDate: warehouseReturns } = warehouseReturnsData;

    function getWarehouseReturnsByState({ state }: { state: WarehouseReturnState }) {
        return warehouseReturns
            .filter((warehouseReturn) => warehouseReturn.state === state)
            .map((warehouseReturn) => {
                return {
                    _id: warehouseReturn._id,
                    content: <WarehouseReturnTrelloItem warehouseReturn={warehouseReturn} />,
                };
            });
    }

    const warehouseReturnsByState = {
        [WarehouseReturnState.Created]: getWarehouseReturnsByState({ state: WarehouseReturnState.Created }),
        [WarehouseReturnState.InProgress]: getWarehouseReturnsByState({ state: WarehouseReturnState.InProgress }),
        [WarehouseReturnState.Delivered]: getWarehouseReturnsByState({ state: WarehouseReturnState.Delivered }),
        [WarehouseReturnState.Stored]: getWarehouseReturnsByState({ state: WarehouseReturnState.Stored }),
    };

    const STATE_COLUMNS_TO_DISPLAY = [
        WarehouseReturnState.Created,
        WarehouseReturnState.InProgress,
        WarehouseReturnState.Delivered,
        WarehouseReturnState.Stored,
    ];

    const initialData = {
        columns: [
            ...STATE_COLUMNS_TO_DISPLAY.map((state) => ({
                title: WAREHOUSE_RETURN_PLURAL_STATES_LABELS[state],
                items: warehouseReturnsByState[state],
            })),
        ],
    };

    return (
        <Content>
            <TrelloViewContainer>
                <TrelloView columnWidth="300px" initialData={initialData} />
            </TrelloViewContainer>
        </Content>
    );
};

const TrelloViewContainer = styled.div`
    overflow-x: scroll;
`;

const Content = styled.div`
    position: relative;
    display: flex;
    flex: 1;
    overflow: hidden;
`;
